import { useState, useEffect, useCallback, Fragment } from "react";
import { api, errMsg, fmt, BREEDS, BREED_LABELS } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { F, In, Sel } from "@/pages/Feed";
import { CoopTree } from "@/components/CoopTree";
import { toast } from "sonner";
import { Plus, Trash2, Warehouse, Egg, Pencil, Check, X, Mail, UserPlus, ShieldCheck, User, Bird } from "lucide-react";

const PERMS = [
  { key: "view_financials", label: "Finans & Para İşlemleri" },
  { key: "manage_shipping", label: "Sevkiyat Yönetimi" },
  { key: "view_customers", label: "↳ Müşterilere Erişim", sub: true },
  { key: "view_central_warehouse_stock", label: "Ana Depo Stoğu" },
];
const PRESETS = [
  { key: "shipping", label: "Sevkiyat & Depo Sorumlusu", perms: { manage_shipping: true, view_customers: true, view_central_warehouse_stock: true, view_financials: false } },
  { key: "barn", label: "Kümes Sorumlusu", perms: { manage_shipping: false, view_customers: false, view_central_warehouse_stock: false, view_financials: false } },
  { key: "finance", label: "Muhasebe / Finans", perms: { view_financials: true, manage_shipping: true, view_customers: true, view_central_warehouse_stock: true } },
  { key: "all", label: "Tüm Yetkiler", perms: { view_financials: true, manage_shipping: true, view_customers: true, view_central_warehouse_stock: true } },
];
export const MODULE_LABELS = { kumes: "Kümes Yönetimi", depot: "Ana Sevk Deposu", sales: "Pazarlama / Satış", accounting: "Ön Muhasebe" };
const ROLES = [
  { key: "kumes", label: "Kümes Sorumlusu" },
  { key: "depot", label: "Ana Sevk Deposu Sorumlusu" },
  { key: "sales", label: "Pazarlamacı / Satış" },
  { key: "accounting", label: "Ön Muhasebe" },
  { key: "admin", label: "Yönetici (Admin)" },
];
const ROLE_LABEL = { kumes: "Kümes Sorumlusu", depot: "Ana Sevk Sorumlusu", sales: "Pazarlamacı", accounting: "Ön Muhasebe" };
const roleLabel = (u) => (u.role === "admin" ? "Yönetici" : (u.modules || ["kumes"]).map((m) => ROLE_LABEL[m]).join(" + "));

function RoleFields({ value, onChange, coops, prefix }) {
  const isAdmin = value.role === "admin";
  const mods = value.modules || [];
  const setRole = (k) => onChange(k === "admin" ? { ...value, role: "admin", modules: ["kumes", "depot", "sales", "accounting"] } : { ...value, role: "operator", modules: [k] });
  const toggleMod = (m) => {
    const next = mods.includes(m) ? mods.filter((x) => x !== m) : [...mods, m];
    if (next.length) onChange({ ...value, modules: next });
  };
  return (
    <div className="space-y-3">
      <div className="max-w-xs">
        <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Rol</label>
        <select value={isAdmin ? "admin" : (mods[0] || "kumes")} onChange={(e) => setRole(e.target.value)} data-testid={`${prefix}-role`}
          className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">
          {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </div>
      {isAdmin ? <div className="text-xs text-muted-foreground">Yönetici tüm modüllere ve sistem/kullanıcı ayarlarına tam erişir.</div> : (
        <>
          <div>
            <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Erişebileceği Modüller (birden fazla seçilebilir)</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(MODULE_LABELS).map(([k, l]) => (
                <button key={k} type="button" onClick={() => toggleMod(k)} data-testid={`${prefix}-module-${k}`}
                  className={`px-3 h-8 rounded-full border text-xs font-semibold transition-colors ${mods.includes(k) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input text-muted-foreground hover:border-primary/50"}`}>{l}</button>
              ))}
            </div>
          </div>
          {mods.includes("kumes") && (
            <>
              <div>
                <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Kümes İçi Yetki Şablonu</label>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((pr) => (
                    <button key={pr.key} type="button" onClick={() => onChange({ ...value, permissions: { ...pr.perms } })} data-testid={`${prefix}-preset-${pr.key}`} className="px-3 h-8 rounded-full border border-input bg-background text-xs font-semibold hover:bg-secondary transition-colors">{pr.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Kümes İçi Yetkiler</label>
                <PermChecks value={value.permissions} onChange={(v) => onChange({ ...value, permissions: v })} prefix={prefix} />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Yetkili Kümesler (çoklu seçim)</label>
                <CoopTree coops={coops} value={value.assigned_coop_ids || []} onChange={(v) => onChange({ ...value, assigned_coop_ids: v })} testidPrefix={`${prefix}-tree`} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function PermChecks({ value, onChange, prefix }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {PERMS.map((p) => (
        <label key={p.key} className={`inline-flex items-center gap-2 text-sm cursor-pointer ${p.sub ? "pl-3 text-muted-foreground" : ""}`} data-testid={`${prefix}-perm-${p.key}`}>
          <input type="checkbox" checked={!!value?.[p.key]} onChange={(e) => onChange({ ...value, [p.key]: e.target.checked })} className="accent-[hsl(var(--primary))] h-4 w-4" />
          <span>{p.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function Settings() {
  const { coops, loadCoops } = useApp();
  const { user: me } = useAuth();
  const activeCoops = coops.filter((c) => c.type === "coop");
  const [form, setForm] = useState({ name: "", type: "coop", description: "", feed_threshold_kg: "" });
  const [editId, setEditId] = useState(null);
  const [editThreshold, setEditThreshold] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [flockThr, setFlockThr] = useState(90);
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState({ email: "", password: "", name: "", role: "operator", modules: ["kumes"], assigned_coop_ids: [], permissions: {} });
  const [editUserId, setEditUserId] = useState(null);
  const [editVal, setEditVal] = useState({});
  const [hideLocked, setHideLocked] = useState(false);
  const [flockEdits, setFlockEdits] = useState({});

  useEffect(() => {
    const m = {};
    coops.filter((c) => c.type === "coop").forEach((c) => {
      m[c.id] = {
        breed: c.breed || "kirmizi",
        breeds: c.breeds?.length ? c.breeds : [c.breed || "kirmizi"],
        chicken_count: c.chicken_count || 0,
        flock_start_date: c.flock_start_date || "",
        flock_start_week: c.flock_start_week || 0,
      };
    });
    setFlockEdits(m);
  }, [coops]);

  const setFlockField = (id, key, val) => setFlockEdits((p) => ({ ...p, [id]: { ...p[id], [key]: val } }));
  const toggleBreed = (id, b) => setFlockEdits((p) => {
    const cur = p[id]?.breeds || [];
    const next = cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b];
    return next.length ? { ...p, [id]: { ...p[id], breeds: next } } : p;
  });
  const saveFlock = async (c) => {
    const e = flockEdits[c.id] || {};
    try {
      await api.put(`/coops/${c.id}`, {
        breeds: e.breeds,
        chicken_count: Number(e.chicken_count || 0),
        chicken_count_date: new Date().toISOString().slice(0, 10),
        flock_start_date: e.flock_start_date || null,
        flock_start_week: Number(e.flock_start_week || 0),
      });
      toast.success(`${c.name} sürü ayarları kaydedildi`);
      loadCoops();
      loadExtra();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const [liveCounts, setLiveCounts] = useState({});
  const loadExtra = useCallback(async () => {
    const [s, u, live] = await Promise.all([api.get("/settings"), api.get("/auth/users"), api.get("/coops/live")]);
    setAlertEmail(s.data?.alert_email || "");
    setFlockThr(s.data?.flock_age_threshold ?? 90);
    setHideLocked(!!s.data?.hide_locked_modules);
    setUsers(u.data);
    setLiveCounts(Object.fromEntries(live.data.map((x) => [x.coop_id, x])));
  }, []);
  useEffect(() => { loadExtra(); }, [loadExtra]);

  const saveAlertEmail = async () => {
    try {
      await api.put("/settings", { alert_email: alertEmail, flock_age_threshold: Number(flockThr), hide_locked_modules: hideLocked });
      toast.success("Ayarlar güncellendi");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addUser = async () => {
    if (!userForm.email || !userForm.password) return toast.error("E-posta ve şifre zorunlu");
    if (userForm.password.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    const isKumes = userForm.role === "operator" && userForm.modules.includes("kumes");
    if (isKumes && userForm.assigned_coop_ids.length === 0) return toast.error("Kümes modülü için en az bir kümes seçin");
    try {
      await api.post("/auth/users", { ...userForm, assigned_coop_ids: isKumes ? userForm.assigned_coop_ids : [], permissions: isKumes ? userForm.permissions : {} });
      toast.success("Kullanıcı eklendi");
      setUserForm({ email: "", password: "", name: "", role: "operator", modules: ["kumes"], assigned_coop_ids: [], permissions: {} });
      loadExtra();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveUserAssignments = async (u) => {
    const isKumes = editVal.role === "operator" && (editVal.modules || []).includes("kumes");
    if (isKumes && !(editVal.assigned_coop_ids || []).length) return toast.error("Kümes modülü için en az bir kümes seçin");
    if (!(editVal.email || "").includes("@")) return toast.error("Geçerli bir e-posta girin");
    if (editVal.password && editVal.password.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalı");
    try {
      await api.put(`/auth/users/${u.id}`, { name: editVal.name, email: editVal.email, ...(editVal.password ? { password: editVal.password } : {}), role: editVal.role, modules: editVal.modules, assigned_coop_ids: isKumes ? editVal.assigned_coop_ids : [], permissions: isKumes ? editVal.permissions : {} });
      toast.success("Kullanıcı bilgileri güncellendi");
      setEditUserId(null);
      loadExtra();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`"${u.email}" silinsin mi?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("Kullanıcı silindi"); loadExtra(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const add = async () => {
    if (!form.name) return toast.error("Lokasyon adı zorunlu");
    try {
      await api.post("/coops", { ...form, feed_threshold_kg: Number(form.feed_threshold_kg || 0) });
      toast.success("Lokasyon eklendi");
      setForm({ name: "", type: "coop", description: "", feed_threshold_kg: "" });
      loadCoops();
    } catch (e) { toast.error("Ekleme başarısız"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`"${c.name}" silinsin mi? İlgili kayıtlar tabloda kalır.`)) return;
    await api.delete(`/coops/${c.id}`);
    toast.success("Lokasyon silindi");
    loadCoops();
  };

  const saveEdit = async (c) => {
    await api.put(`/coops/${c.id}`, { name: c.name, type: c.type, description: c.description || "", feed_threshold_kg: Number(editThreshold || 0) });
    toast.success("Eşik güncellendi");
    setEditId(null);
    loadCoops();
  };

  return (
    <div>
      <PageHeader title="Ayarlar & Lokasyonlar" subtitle="Lokasyonlar, kullanıcılar ve otomatik uyarı e-postası ayarları" />

      <div className="rounded-xl border border-border bg-card p-5 mb-6" data-testid="alert-email-card">
        <div className="flex items-center gap-2 mb-1"><Mail size={18} className="text-primary" /><h3 className="font-head font-semibold">Otomatik Uyarı E-postası</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Kritik yem stoğu veya arınma süresi durumunda anlık uyarı gönderilecek yönetici adresi.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-72"><F label="Yönetici E-posta"><In type="email" value={alertEmail} onChange={setAlertEmail} testid="alert-email-input" /></F></div>
          <div className="w-52"><F label="Sürü Yaşı Uyarı Eşiği (hafta)"><In type="number" value={flockThr} onChange={setFlockThr} testid="flock-threshold-input" /></F></div>
          <label className="h-9 inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="hide-locked-toggle">
            <input type="checkbox" checked={hideLocked} onChange={(e) => setHideLocked(e.target.checked)} className="accent-[hsl(var(--primary))] h-4 w-4" />
            Yetkisiz modülleri tamamen gizle (kapalıysa kilitli görünür)
          </label>
          <button onClick={saveAlertEmail} data-testid="alert-email-save" className="h-9 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 text-sm font-semibold hover:bg-primary/90 transition-colors"><Check size={16} /> Kaydet</button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 mb-6 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="coop-add-form">
        <F label="Lokasyon Adı"><In value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="coop-name" /></F>
        <F label="Tür">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="coop-type"
            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">
            <option value="coop">Kümes</option>
            <option value="warehouse">Depo</option>
          </select>
        </F>
        <F label="Açıklama"><In value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="coop-desc" /></F>
        <F label="Kritik Yem Eşiği (kg)"><In type="number" value={form.feed_threshold_kg} onChange={(v) => setForm({ ...form, feed_threshold_kg: v })} testid="coop-threshold" /></F>
        <button onClick={add} data-testid="coop-add" className="h-9 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> Ekle</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {coops.map((c) => (
          <div key={c.id} className="rounded-xl border border-border bg-card p-5" data-testid={`coop-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${c.type === "warehouse" ? "bg-amber-500/10 text-amber-500" : "bg-primary/10 text-primary"}`}>
                  {c.type === "warehouse" ? <Warehouse size={20} /> : <Egg size={20} />}
                </div>
                <div>
                  <div className="font-head font-semibold">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.description || (c.type === "warehouse" ? "Depo" : "Kümes")}</div>
                </div>
              </div>
              <button onClick={() => remove(c)} data-testid={`coop-delete-${c.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16} /></button>
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Kritik Yem Eşiği</span>
              {editId === c.id ? (
                <div className="flex items-center gap-1">
                  <input type="number" value={editThreshold} onChange={(e) => setEditThreshold(e.target.value)} data-testid={`coop-edit-input-${c.id}`}
                    className="h-8 w-24 rounded-md border border-input bg-background px-2 text-sm text-right focus:ring-2 focus:ring-primary outline-none" />
                  <button onClick={() => saveEdit(c)} data-testid={`coop-edit-save-${c.id}`} className="text-primary p-1"><Check size={16} /></button>
                  <button onClick={() => setEditId(null)} className="text-muted-foreground p-1"><X size={16} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="tabular-nums font-medium">{c.feed_threshold_kg} kg</span>
                  <button onClick={() => { setEditId(c.id); setEditThreshold(String(c.feed_threshold_kg)); }} data-testid={`coop-edit-${c.id}`} className="text-muted-foreground hover:text-primary transition-colors"><Pencil size={14} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8" data-testid="flock-mgmt">
        <div className="flex items-center gap-2 mb-1"><Bird size={18} className="text-primary" /><h3 className="font-head font-semibold">Kümes / Sürü Yönetimi</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Her alt kümes için tavuk çeşidi (ırk — birden fazla seçilebilir, ★ ana çeşit; fire ana çeşide yazılır), güncel tavuk sayısı ve sürü referans tarihi. Sürü yaşı Günlük Üretim'de tarihe göre otomatik hesaplanır.</p>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/60 text-left">
                {["Alt Kümes", "Tavuk Çeşidi", "Başlangıç / Kalibrasyon", "Son Güncel Durum", "Sürü Referans Tarihi", "Başlangıç Haftası", ""].map((h, i) => <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {activeCoops.map((c) => {
                  const e = flockEdits[c.id] || {};
                  return (
                    <tr key={c.id} className="border-t border-border hover:bg-secondary/30" data-testid={`flock-row-${c.id}`}>
                      <td className="px-3 py-2.5 font-medium whitespace-nowrap">{c.name}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1.5" data-testid={`flock-breed-${c.id}`}>
                          {BREEDS.map((b) => {
                            const idx = (e.breeds || []).indexOf(b);
                            return (
                              <button key={b} type="button" onClick={() => toggleBreed(c.id, b)} data-testid={`flock-breed-${c.id}-${b}`}
                                title={idx === 0 ? "Ana çeşit (fire bu çeşide yazılır)" : ""}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${idx >= 0 ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                                {BREED_LABELS[b]}{idx === 0 && (e.breeds || []).length > 1 ? " ★" : ""}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <input type="number" min="0" value={e.chicken_count ?? 0} onChange={(ev) => setFlockField(c.id, "chicken_count", ev.target.value)} data-testid={`flock-count-${c.id}`}
                          className="h-9 w-28 rounded-md border border-input bg-background px-2 text-sm text-right tabular-nums focus:ring-2 focus:ring-primary outline-none" />
                      </td>
                      <td className="px-3 py-2.5" data-testid={`flock-live-${c.id}`}>
                        {(() => { const lv = liveCounts[c.id]; return (
                          <span className="inline-flex flex-col">
                            <span className="text-sm font-semibold tabular-nums text-primary">{fmt(lv?.current_count ?? (e.chicken_count || 0))} Adet Canlı</span>
                            <span className="text-[11px] text-muted-foreground">{lv?.last_date ? `En son kayıt: ${lv.last_date}` : "Kayıt yok (taban)"}</span>
                          </span>
                        ); })()}
                      </td>
                      <td className="px-3 py-2.5">
                        <input type="date" value={e.flock_start_date || ""} onChange={(ev) => setFlockField(c.id, "flock_start_date", ev.target.value)} data-testid={`flock-date-${c.id}`}
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none" />
                      </td>
                      <td className="px-3 py-2.5">
                        <input type="number" min="0" value={e.flock_start_week ?? 0} onChange={(ev) => setFlockField(c.id, "flock_start_week", ev.target.value)} data-testid={`flock-week-${c.id}`}
                          className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm text-right tabular-nums focus:ring-2 focus:ring-primary outline-none" />
                      </td>
                      <td className="px-3 py-2.5">
                        <button onClick={() => saveFlock(c)} data-testid={`flock-save-${c.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors"><Check size={14} /> Kaydet</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-primary" /><h3 className="font-head font-semibold">Kullanıcılar & Roller</h3></div>
        <div className="rounded-xl border border-border bg-card p-5 mb-4" data-testid="user-add-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end mb-3">
            <F label="Ad Soyad"><In value={userForm.name} onChange={(v) => setUserForm({ ...userForm, name: v })} testid="user-name" /></F>
            <F label="E-posta"><In type="email" value={userForm.email} onChange={(v) => setUserForm({ ...userForm, email: v })} testid="user-email" /></F>
            <F label="Şifre"><In type="text" value={userForm.password} onChange={(v) => setUserForm({ ...userForm, password: v })} testid="user-password" /></F>
          </div>
          <div className="mb-3"><RoleFields value={userForm} onChange={setUserForm} coops={coops} prefix="user" /></div>
          <button onClick={addUser} data-testid="user-add" className="h-9 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><UserPlus size={16} /> Ekle</button>
        </div>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-secondary/60 text-left">
              {["Kullanıcı", "E-posta", "Rol", "Yetkili Kümesler", ""].map((h, i) => <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody>
              {users.map((u) => {
                const assigned = u.assigned_coop_ids || (u.coop_id ? [u.coop_id] : []);
                return (
                <Fragment key={u.id}>
                <tr className="border-t border-border hover:bg-secondary/30" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2.5"><span className="inline-flex items-center gap-2 font-medium"><User size={14} className="text-muted-foreground" />{u.name || "-"}</span></td>
                  <td className="px-3 py-2.5">{u.email}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-amber-500/10 text-amber-600"}`} data-testid={`user-role-badge-${u.id}`}>
                      {roleLabel(u)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {u.role === "admin" ? <span className="text-muted-foreground">Tüm modüller · Tüm yetkiler</span> : (
                      <div className="space-y-1">
                        <span className="text-xs block">{(u.modules || ["kumes"]).includes("kumes") ? (assigned.length === 0 ? "-" : assigned.map((id) => coops.find((c) => c.id === id)?.name).filter(Boolean).join(", ")) : "—"}</span>
                        <div className="flex flex-wrap gap-1">
                          {PERMS.filter((p) => u.permissions?.[p.key]).map((p) => (
                            <span key={p.key} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary" data-testid={`user-perm-badge-${u.id}-${p.key}`}>{p.label}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      {u.id !== me?.id && (
                        <button onClick={() => { setEditUserId(editUserId === u.id ? null : u.id); setEditVal({ name: u.name || "", email: u.email || "", password: "", role: u.role, modules: u.modules || ["kumes"], assigned_coop_ids: assigned, permissions: u.permissions || {} }); }} data-testid={`user-edit-${u.id}`} className="text-muted-foreground hover:text-primary transition-colors" title="Kullanıcıyı düzenle"><Pencil size={15} /></button>
                      )}
                      {u.id !== me?.id && <button onClick={() => removeUser(u)} data-testid={`user-delete-${u.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16} /></button>}
                    </div>
                  </td>
                </tr>
                {editUserId === u.id && (
                  <tr className="border-t border-border bg-secondary/20" data-testid={`user-edit-panel-${u.id}`}>
                    <td colSpan={5} className="px-3 py-3 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <F label="Ad Soyad"><In value={editVal.name} onChange={(v) => setEditVal({ ...editVal, name: v })} testid={`edit-${u.id}-name`} /></F>
                        <F label="E-posta"><In type="email" value={editVal.email} onChange={(v) => setEditVal({ ...editVal, email: v })} testid={`edit-${u.id}-email`} /></F>
                        <F label="Yeni Şifre (değişmeyecekse boş bırakın)"><In type="password" value={editVal.password} onChange={(v) => setEditVal({ ...editVal, password: v })} testid={`edit-${u.id}-password`} /></F>
                      </div>
                      <RoleFields value={editVal} onChange={setEditVal} coops={coops} prefix={`edit-${u.id}`} />
                      <div className="flex gap-2">
                        <button onClick={() => saveUserAssignments(u)} data-testid={`user-edit-save-${u.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:bg-primary/90"><Check size={14} /> Kaydet</button>
                        <button onClick={() => setEditUserId(null)} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold"><X size={14} /> İptal</button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );})}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
